import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { supportCaseAPI } from '../services/api';
import { hasPermission, isActingAsSupportAgent, canTriage } from '../utils/permissions';
import { SIMULATE_CHANGED_EVENT } from '../services/simulate';
import { CASE_PRIORITIES } from '../constants/supportCases';
import CaseHero from '../components/CaseHero';
import StatusStepper from '../components/StatusStepper';
import CaseAttachments from '../components/CaseAttachments';
import CaseTimeline from '../components/CaseTimeline';
import { PaperclipIcon } from '../components/icons';
import './SupportCases.css';

/**
 * Support case detail: case fields + collaboration thread + add-comment box.
 * Read access is enforced server-side (owner or support_cases.view_all).
 */
function SupportCaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const [supportCase, setSupportCase] = useState(null);
  const [comments, setComments] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [replyFile, setReplyFile] = useState(null);
  const [posting, setPosting] = useState(false);
  const [agents, setAgents] = useState([]);
  const [triageSaving, setTriageSaving] = useState(false);
  const fileInputRef = useRef(null);

  const listPath = tenantPath('/lists/support-cases');
  const canComment = hasPermission('support_cases.comment');
  const canDeleteCase = hasPermission('support_cases.delete');
  // Super admins (support agents) get the full case picture even from this tenant view —
  // but while "View as" is simulating another role, they preview the true end-user experience.
  const isSupportAgent = isActingAsSupportAgent();
  // A tenant triager can change status/priority and assign the case to a CFC agent.
  const isTriager = canTriage();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [caseRes, commentsRes, timelineRes] = await Promise.all([
        supportCaseAPI.getById(id),
        supportCaseAPI.listComments(id),
        supportCaseAPI.listTimeline(id),
      ]);
      setSupportCase(caseRes.data?.data ?? null);
      setComments(commentsRes.data?.data ?? []);
      setEvents(timelineRes.data?.data ?? []);
    } catch (err) {
      console.error('Error loading support case:', err);
      setError(err.response?.data?.error || err.message);
      setSupportCase(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Re-fetch when "View as" is toggled so the view flips between the agent and end-user
  // experience without a manual refresh (matches ClientList / WorkTasksDashboard).
  useEffect(() => {
    const handler = () => load();
    window.addEventListener(SIMULATE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(SIMULATE_CHANGED_EVENT, handler);
  }, [load]);

  // Triagers need the CFC agent list to populate the assignment dropdown.
  useEffect(() => {
    if (!isTriager) return undefined;
    let cancelled = false;
    supportCaseAPI.listAgents()
      .then((res) => { if (!cancelled) setAgents(res.data?.data ?? []); })
      .catch(() => { if (!cancelled) setAgents([]); });
    return () => { cancelled = true; };
  }, [isTriager]);

  const triage = async (patch) => {
    try {
      setTriageSaving(true);
      setError(null);
      await supportCaseAPI.triage(id, patch);
      await load();
    } catch (err) {
      console.error('Error updating case:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update case');
    } finally {
      setTriageSaving(false);
    }
  };

  const handleDeleteCase = async () => {
    if (!window.confirm('Delete this case? This permanently removes the case, its comments, and attachments.')) return;
    try {
      await supportCaseAPI.deleteCase(id);
      navigate(listPath);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete case');
    }
  };

  const handleAddComment = async () => {
    const body = newComment.trim();
    if (!body || posting) return;
    try {
      setPosting(true);
      const res = await supportCaseAPI.addComment(id, { body });
      const commentId = res.data?.data?.id;
      if (replyFile && commentId) {
        await supportCaseAPI.uploadAttachment(id, replyFile, commentId);
      }
      setNewComment('');
      setReplyFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      // Reload the thread (and case, whose updated_at moved).
      await load();
    } catch (err) {
      console.error('Error adding comment:', err);
      setError(err.response?.data?.error || err.message || 'Failed to add comment');
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return <div className="support-case-detail-page"><p>Loading case…</p></div>;
  }

  if (error && !supportCase) {
    return (
      <div className="support-case-detail-page">
        <Link to={listPath} className="support-case-back-link">← Back to cases</Link>
        <div className="support-cases-error" role="alert">{error}</div>
      </div>
    );
  }

  if (!supportCase) return null;

  // Assignment targets the CFC agents; keep the current assignee selectable even if the
  // list hasn't loaded or they're no longer in it.
  const assigneeOptions = [...agents];
  if (supportCase.assigned_agent_user_id
      && !agents.some((a) => String(a.id) === String(supportCase.assigned_agent_user_id))) {
    assigneeOptions.push({
      id: supportCase.assigned_agent_user_id,
      name: supportCase.assigned_agent_name || supportCase.assigned_agent_username || `User #${supportCase.assigned_agent_user_id}`,
    });
  }

  return (
    <div className="support-case-detail-page">
      <Link to={listPath} className="support-case-back-link">← Back to cases</Link>
      <CaseHero supportCase={supportCase} showAssignee={isSupportAgent || isTriager} />

      {isTriager ? (
        <>
          <StatusStepper
            status={supportCase.status}
            editable
            disabled={triageSaving}
            onChange={(v) => triage({ status: v })}
          />
          <div className="support-case-triage-controls">
            <label>
              <span>Priority</span>
              <select
                value={supportCase.priority || ''}
                disabled={triageSaving}
                onChange={(e) => triage({ priority: e.target.value })}
              >
                <option value="">— (unset)</option>
                {CASE_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </label>
            <label>
              <span>Assignment</span>
              <select
                value={supportCase.assigned_agent_user_id || ''}
                disabled={triageSaving}
                onChange={(e) => triage({ assigned_agent_user_id: e.target.value || null })}
              >
                <option value="">Unassigned</option>
                {assigneeOptions.map((a) => <option key={a.id} value={a.id}>{a.name || a.username}</option>)}
              </select>
            </label>
          </div>
        </>
      ) : (
        <StatusStepper status={supportCase.status} />
      )}

      {supportCase.description?.trim() && (
        <div className="support-case-description">
          {supportCase.description.split('\n').map((line, i) => (
            <p key={i}>{line || ' '}</p>
          ))}
        </div>
      )}

      <CaseAttachments caseId={id} api={supportCaseAPI} canUpload={canComment} />

      <h2 className="support-case-thread-title">Timeline</h2>
      <CaseTimeline
        caseId={id}
        comments={comments}
        events={events}
        fetchBlob={supportCaseAPI.attachmentBlob}
        showInternal={isSupportAgent}
      />

      {error && supportCase && <div className="support-cases-error" role="alert">{error}</div>}

      {canComment ? (
        <div className="support-case-add-comment">
          <textarea
            value={newComment}
            rows={4}
            placeholder="Add a comment for more context or to collaborate on a solution…"
            onChange={(e) => setNewComment(e.target.value)}
          />
          <div className="support-case-add-comment-actions">
            <label className="support-case-attach-link">
              <PaperclipIcon size={15} /> {replyFile ? replyFile.name : 'Attach file'}
              <input ref={fileInputRef} type="file" hidden disabled={posting}
                onChange={(e) => setReplyFile(e.target.files?.[0] || null)} />
            </label>
            <button
              type="button"
              className="btn-save"
              onClick={handleAddComment}
              disabled={newComment.trim() === '' || posting}
            >
              {posting ? 'Posting…' : 'Add comment'}
            </button>
          </div>
        </div>
      ) : (
        <p className="support-case-thread-empty">You don’t have permission to comment on this case.</p>
      )}

      {canDeleteCase && (
        <div className="support-case-danger">
          <button type="button" className="support-case-delete" onClick={handleDeleteCase}>Delete case</button>
        </div>
      )}
    </div>
  );
}

export default SupportCaseDetail;
